import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useWallet } from "../context/WalletContext";
import api, { API } from "../lib/api";
import { Trash2, Tag, X, Wallet } from "lucide-react";
import { toast } from "sonner";
import { formatIDR } from "../lib/format";
import PinModal from "../components/PinModal";

const BACKEND = API.replace(/\/api$/, "");
const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
};

export default function Cart() {
  const { items, remove, updateQty, syncPrice, total, clear } = useCart();

  const { user } = useAuth();
  const wallet = useWallet();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [coupon, setCoupon] = useState({ code: "", discount: 0, valid: false });
  const [couponInput, setCouponInput] = useState("");
  const [sellerCoupon, setSellerCoupon] = useState({ code: "", discount: 0, valid: false });
  const [sellerCouponInput, setSellerCouponInput] = useState("");
  const [applying, setApplying] = useState(false);

  const [channels, setChannels] = useState([]);
  const [method, setMethod] = useState("WALLET");
  const [phone, setPhone] = useState("");
  const [loadingChannels, setLoadingChannels] = useState(true);

  useEffect(() => {
    api.get("/payment-channels")
      .then((r) => {
        setChannels(r.data);
      })
      .catch(() => toast.error("Gagal memuat metode pembayaran"))
      .finally(() => setLoadingChannels(false));
  }, []);

  // Refresh harga tiap item keranjang saat halaman dibuka. Flash sale bisa
  // berakhir / kehabisan stok sementara item sudah lama nangkring di cart,
  // jadi total yang ditampilkan harus ikut balik ke harga normal. Server tetap
  // jadi sumber kebenaran harga saat checkout.
  useEffect(() => {
    const ids = items.map((it) => it.product_id);
    if (ids.length === 0) return;
    ids.forEach((pid) => {
      api
        .get(`/products/${pid}`)
        .then((r) => syncPrice(pid, r.data))
        .catch(() => {});
    });
    // Sengaja hanya jalan sekali saat mount supaya tidak looping dengan
    // perubahan `items` yang dipicu syncPrice itu sendiri.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const finalTotal = Math.max(0, total - coupon.discount - sellerCoupon.discount);

  const itemPayload = items.map((it) => ({
    product_id: it.product_id,
    price: it.price,
    quantity: it.quantity,
    seller_id: it.seller_id,
  }));

  const applyCoupon = async (scope) => {
    const input = scope === "admin" ? couponInput : sellerCouponInput;
    if (!input.trim()) return;
    setApplying(true);
    try {
      const { data } = await api.post("/coupons/validate", {
        code: input.trim().toUpperCase(),
        subtotal: total,
        scope,
        items: itemPayload,
      });
      if (scope === "admin") {
        setCoupon({ code: data.code, discount: data.discount_amount, valid: true });
      } else {
        setSellerCoupon({ code: data.code, discount: data.discount_amount, valid: true });
      }
      toast.success(`Voucher ${data.code} berhasil diterapkan`);
    } catch (err) {
      if (scope === "admin") setCoupon({ code: "", discount: 0, valid: false });
      else setSellerCoupon({ code: "", discount: 0, valid: false });
      toast.error(err.response?.data?.detail || "Voucher tidak valid");
    } finally {
      setApplying(false);
    }
  };

  const removeCoupon = (scope) => {
    if (scope === "admin") {
      setCoupon({ code: "", discount: 0, valid: false });
      setCouponInput("");
    } else {
      setSellerCoupon({ code: "", discount: 0, valid: false });
      setSellerCouponInput("");
    }
  };

  const [pinMode, setPinMode] = useState(null);

  // Item yang dikirim ke server saat checkout. ``order_fields`` membawa User ID
  // / Zone ID untuk produk instan undeadshopp; server memvalidasi ulang dan
  // meneruskannya ke API topup setelah pembayaran lunas.
  const checkoutItems = () =>
    items.map((it) => ({
      product_id: it.product_id,
      quantity: it.quantity,
      ...(it.order_fields ? { order_fields: it.order_fields } : {}),
    }));

  const doCheckout = async (pin) => {
    setLoading(true);
    try {
      if (method === "WALLET") {
        const { data } = await api.post("/checkout/wallet", {
          items: checkoutItems(),

          origin_url: window.location.origin,
          coupon_code: coupon.valid ? coupon.code : null,
          seller_coupon_code: sellerCoupon.valid ? sellerCoupon.code : null,
          pin,
        });
        toast.success("Pembayaran berhasil! Order sedang diproses.");
        wallet.refresh && wallet.refresh();
        clear();
        navigate(`/checkout/success?merchant_ref=${data.merchant_ref}`);
        return;
      }
      const { data } = await api.post("/checkout/session", {
        items: checkoutItems(),
        origin_url: window.location.origin,
        coupon_code: coupon.valid ? coupon.code : null,
        seller_coupon_code: sellerCoupon.valid ? sellerCoupon.code : null,
        payment_method: method,
        customer_phone: phone || "081234567890",
        pin,
      });
      window.location.href = data.checkout_url || data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  // Deteksi item dari toko yang sedang tutup. Cart tetap menampilkan item
  // tsb (user perlu tahu kenapa), tapi checkout button di-disable supaya
  // error dari server tidak terjadi di ujung. Backend juga sudah memvalidasi
  // shop_open di /checkout/session & /checkout/wallet, jadi ini cuma UX.
  const closedShopItems = items.filter((it) => it.seller_is_open === false);
  const hasClosedShopItem = closedShopItems.length > 0;

  const checkout = () => {
    if (!user) return navigate("/login");
    if (items.length === 0) return;
    if (hasClosedShopItem) {
      toast.error(`${closedShopItems.length} item berasal dari toko yang sedang tutup. Hapus dulu untuk melanjutkan.`);
      return;
    }
    if (!method) { toast.error("Pilih metode pembayaran"); return; }
    setPinMode(user?.has_pin ? "verify" : "set");
  };

  // Group channels by group
  const groupedChannels = channels.reduce((acc, c) => {
    const g = c.group || "Other";
    if (!acc[g]) acc[g] = [];
    acc[g].push(c);
    return acc;
  }, {});

  return (
    <div data-testid="cart-page" className="max-w-7xl mx-auto px-4 md:px-8 py-12">
      <span className="tag tag-accent">// CHECKOUT</span>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white mt-3 mb-8">Keranjang Kamu</h1>
      {items.length === 0 ? (
        <div data-testid="empty-cart" className="border border-white/10 p-12 text-center">
          <p className="text-white/60 mb-4">Keranjang kamu kosong.</p>
          <Link to="/browse" className="btn-primary inline-block">Mulai Belanja</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-8">
          <div className="space-y-6">
            <div className="space-y-3">
              {hasClosedShopItem && (
                <div
                  data-testid="cart-closed-shop-warning"
                  className="border border-[#FF003C]/50 bg-[#FF003C]/10 text-[#FFB3B3] px-4 py-3 text-sm flex items-center gap-2"
                >
                  <span className="text-lg">⚠️</span>
                  <div>
                    <div className="font-bold text-white">
                      {closedShopItems.length} item dari toko yang sedang tutup
                    </div>
                    <div className="text-xs">
                      Hapus item-item di bawah ini (atau tunggu toko buka) untuk melanjutkan checkout.
                    </div>
                  </div>
                </div>
              )}
              {items.map((it) => (
                <div
                  key={it.product_id}
                  data-testid={`cart-item-${it.product_id}`}
                  data-shop-open={it.seller_is_open === false ? "false" : "true"}
                  className={`border p-4 flex gap-4 items-center ${
                    it.seller_is_open === false
                      ? "border-[#FF003C]/40 bg-[#FF003C]/5 opacity-80"
                      : "border-white/10"
                  }`}
                >
                  <div className="w-20 h-20 bg-black border border-white/10 overflow-hidden shrink-0 relative">
                    {it.image_url && <img src={resolveImg(it.image_url)} alt={it.title} className={`w-full h-full object-cover ${it.seller_is_open === false ? "grayscale" : ""}`} />}
                    {it.seller_is_open === false && (
                      <div data-testid={`cart-shop-closed-${it.product_id}`} className="absolute inset-0 flex items-center justify-center bg-black/70 text-[#FF003C] text-[9px] font-black uppercase tracking-widest">
                        Toko Tutup
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white truncate">{it.title}</h3>
                    <p className="text-xs text-white/50">{it.seller_name}</p>
                    {it.seller_is_open === false && (
                      <p data-testid={`cart-shop-closed-msg-${it.product_id}`} className="text-[11px] text-[#FF003C] mt-1">
                        Toko sedang tutup. Hapus item ini untuk melanjutkan checkout.
                      </p>
                    )}
                    {/* Kalau item sedang kena flash sale, tampilkan harga
                        flash + harga coret supaya buyer sadar hematnya. */}
                    {it.flash_sale_id && it.normal_price > it.price ? (
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <span className="font-display font-bold text-[#FF003C]">{formatIDR(it.price)}</span>
                        <span className="text-xs text-white/40 line-through">{formatIDR(it.normal_price)}</span>
                        <span className="text-[9px] font-black uppercase tracking-widest bg-[#FF003C] text-white px-1.5 py-0.5">
                          Flash Sale
                        </span>
                      </div>
                    ) : (
                      <div className="font-display font-bold text-[#00F0FF] mt-1">{formatIDR(it.price)}</div>
                    )}
                    {/* Tujuan topup untuk produk instan — ditampilkan supaya
                        buyer bisa cek ulang ID sebelum bayar. */}
                    {it.order_fields?.user_id && (
                      <p
                        data-testid={`cart-target-${it.product_id}`}
                        className="text-[11px] text-white/50 mt-1"
                      >
                        Tujuan: <span className="text-[#00F0FF] font-mono">{it.order_fields.user_id}</span>
                        {it.order_fields.zone_id ? ` (${it.order_fields.zone_id})` : ""}
                      </p>
                    )}


                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="w-8 h-8 border border-white/15 hover:bg-white/5">−</button>
                    <span className="w-8 text-center" data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                    <button onClick={() => updateQty(it.product_id, it.quantity + 1)} className="w-8 h-8 border border-white/15 hover:bg-white/5">+</button>
                  </div>
                  <button data-testid={`cart-remove-${it.product_id}`} onClick={() => remove(it.product_id)} className="text-white/40 hover:text-[#FF003C] p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Payment method picker */}
            <div className="border border-white/10 p-6">
              <h3 className="font-display font-bold text-xl text-white mb-1 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-[#00F0FF]" /> Metode Pembayaran
              </h3>
              <p className="text-xs uppercase tracking-[0.2em] text-white/40 mb-4">Bayar instan dengan saldo · atau lewat Tripay</p>

              {/* Wallet option (featured) */}
              {(() => {
                const balance = Number(wallet?.balance || 0);
                const insufficient = balance < finalTotal;
                return (
                  <label
                    data-testid="channel-WALLET"
                    className={`flex items-center gap-3 p-4 cursor-pointer border-2 transition-colors mb-4 ${
                      method === "WALLET"
                        ? "border-[#00FF66] bg-[#00FF66]/10"
                        : "border-white/15 hover:border-white/30"
                    } ${insufficient && method === "WALLET" ? "border-[#FF003C]" : ""}`}
                  >
                    <input
                      type="radio"
                      name="payment_method"
                      value="WALLET"
                      checked={method === "WALLET"}
                      onChange={() => setMethod("WALLET")}
                      className="accent-[#00FF66]"
                    />
                    <Wallet className="w-6 h-6 text-[#00FF66]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white flex items-center gap-2">
                        Bayar dengan Saldo
                        <span className="text-[10px] bg-[#00FF66] text-black font-black px-1.5 py-0.5 uppercase tracking-widest">Instan</span>
                      </p>
                      <p className="text-[11px] text-white/60 mt-0.5">
                        Saldo kamu: <span data-testid="wallet-balance-display" className={insufficient ? "text-[#FF003C] font-bold" : "text-[#00F0FF] font-bold"}>{formatIDR(balance)}</span>
                        {insufficient && (
                          <> · <span className="text-[#FF003C]">Kurang {formatIDR(finalTotal - balance)}</span> · <Link to="/wallet" className="underline text-[#FFD700]">Top-up</Link></>
                        )}
                      </p>
                    </div>
                  </label>
                );
              })()}

              {loadingChannels ? (
                <p className="text-white/50 text-sm">Loading channels...</p>
              ) : channels.length === 0 ? (
                <p className="text-white/50 text-sm">Tidak ada channel aktif</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(groupedChannels).map(([group, list]) => (
                    <div key={group}>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{group}</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {list.map((c) => (
                          <label
                            key={c.code}
                            data-testid={`channel-${c.code}`}
                            className={`flex items-center gap-2 p-3 cursor-pointer border transition-colors ${
                              method === c.code
                                ? "border-[#00F0FF] bg-[#00F0FF]/10"
                                : "border-white/15 hover:border-white/30"
                            }`}
                          >
                            <input
                              type="radio"
                              name="payment_method"
                              value={c.code}
                              checked={method === c.code}
                              onChange={() => setMethod(c.code)}
                              className="accent-[#00F0FF]"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{c.name}</p>
                              <p className="text-[10px] text-white/40 uppercase tracking-widest">{c.code}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4">
                <label className="text-xs uppercase tracking-[0.2em] text-white/60">No. HP (untuk e-wallet)</label>
                <input
                  data-testid="phone-input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="081234567890"
                  className="w-full bg-black border border-white/15 focus:border-[#00F0FF] text-white text-sm px-3 py-2.5 mt-2"
                />
              </div>
            </div>
          </div>

          <aside className="border border-white/10 p-6 h-fit">
            <h3 className="font-display font-bold text-xl text-white mb-4">Ringkasan Pesanan</h3>

            <div className="mb-4 pb-4 border-b border-white/10 space-y-3">
              {/* Web (admin) voucher */}
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/60 flex items-center gap-2">
                  <Tag className="w-3 h-3 text-[#00F0FF]" /> Voucher Web
                </label>
                {!coupon.valid ? (
                  <div className="flex gap-2 mt-2">
                    <input
                      data-testid="coupon-web-input"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="KODE PROMO WEB"
                      className="flex-1 bg-black border border-white/15 focus:border-[#00F0FF] text-white text-sm px-3 py-2 uppercase"
                    />
                    <button data-testid="coupon-web-apply" onClick={() => applyCoupon("admin")} disabled={applying} className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs uppercase font-bold tracking-widest disabled:opacity-50">
                      {applying ? "..." : "Apply"}
                    </button>
                  </div>
                ) : (
                  <div data-testid="coupon-web-applied" className="mt-2 flex items-center justify-between border border-[#00F0FF]/30 bg-[#00F0FF]/10 px-3 py-2 text-sm">
                    <span className="font-mono text-[#00F0FF]">{coupon.code} · −{formatIDR(coupon.discount)}</span>
                    <button data-testid="coupon-web-remove" onClick={() => removeCoupon("admin")} className="text-white/60 hover:text-[#FF003C]">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <p className="text-[10px] text-white/40 mt-1">Voucher dari Ustore (berlaku untuk semua produk)</p>
              </div>

              {/* Seller voucher */}
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-white/60 flex items-center gap-2">
                  <Tag className="w-3 h-3 text-[#FFD700]" /> Voucher Seller
                </label>
                {!sellerCoupon.valid ? (
                  <div className="flex gap-2 mt-2">
                    <input
                      data-testid="coupon-seller-input"
                      value={sellerCouponInput}
                      onChange={(e) => setSellerCouponInput(e.target.value.toUpperCase())}
                      placeholder="KODE PROMO SELLER"
                      className="flex-1 bg-black border border-white/15 focus:border-[#FFD700] text-white text-sm px-3 py-2 uppercase"
                    />
                    <button data-testid="coupon-seller-apply" onClick={() => applyCoupon("seller")} disabled={applying} className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-xs uppercase font-bold tracking-widest disabled:opacity-50">
                      {applying ? "..." : "Apply"}
                    </button>
                  </div>
                ) : (
                  <div data-testid="coupon-seller-applied" className="mt-2 flex items-center justify-between border border-[#FFD700]/30 bg-[#FFD700]/10 px-3 py-2 text-sm">
                    <span className="font-mono text-[#FFD700]">{sellerCoupon.code} · −{formatIDR(sellerCoupon.discount)}</span>
                    <button data-testid="coupon-seller-remove" onClick={() => removeCoupon("seller")} className="text-white/60 hover:text-[#FF003C]">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <p className="text-[10px] text-white/40 mt-1">Voucher dari seller (hanya berlaku untuk produk seller tsb)</p>
              </div>
            </div>

            <div className="space-y-2 text-sm border-b border-white/10 pb-4 mb-4">
              <div className="flex justify-between text-white/70">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">{formatIDR(total)}</span>
              </div>
              {coupon.valid && (
                <div className="flex justify-between text-[#00F0FF]">
                  <span>Voucher Web ({coupon.code})</span>
                  <span>−{formatIDR(coupon.discount)}</span>
                </div>
              )}
              {sellerCoupon.valid && (
                <div className="flex justify-between text-[#FFD700]">
                  <span>Voucher Seller ({sellerCoupon.code})</span>
                  <span>−{formatIDR(sellerCoupon.discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-white/70">
                <span>Biaya layanan</span>
                <span>Rp 0</span>
              </div>
            </div>
            <div className="flex justify-between mb-6">
              <span className="text-xs uppercase tracking-[0.2em] text-white/60">Total</span>
              <span data-testid="cart-total" className="font-display font-black text-2xl text-[#00F0FF]">
                {formatIDR(finalTotal)}
              </span>
            </div>
            <button
              data-testid="checkout-btn"
              disabled={loading || hasClosedShopItem || (method !== "WALLET" && channels.length === 0) || (method === "WALLET" && Number(wallet?.balance || 0) < finalTotal)}
              onClick={checkout}
              title={hasClosedShopItem ? "Hapus item dari toko yang tutup dulu" : undefined}
              className={`w-full disabled:opacity-50 font-display font-black uppercase tracking-widest px-6 py-3 text-sm ${method === "WALLET" ? "bg-[#00FF66] hover:bg-[#33FF88] text-black" : "btn-primary"}`}
            >
              {loading
                ? "Memproses..."
                : hasClosedShopItem
                  ? "Tidak bisa checkout — toko tutup"
                  : method === "WALLET"
                    ? `Bayar Sekarang · ${formatIDR(finalTotal)}`
                    : `Bayar via ${method || "Tripay"}`}
            </button>
            <p className="text-[10px] uppercase tracking-widest text-white/30 mt-3 text-center">
              {method === "WALLET" ? "Pembayaran instan dari saldo akun" : "Pembayaran diproses oleh Tripay"}
            </p>
            <button onClick={clear} className="text-xs uppercase tracking-widest text-white/40 hover:text-white mt-3 w-full">
              Kosongkan keranjang
            </button>
          </aside>
        </div>
      )}
      {pinMode && (
        <PinModal
          mode={pinMode}
          isChange={false}
          title={pinMode === "set" ? "Setel PIN Transaksi" : "Konfirmasi Checkout"}
          description={pinMode === "set" ? "Buat PIN 6 digit untuk setiap transaksi" : `Bayar ${formatIDR(total)} via ${method}`}
          onClose={() => setPinMode(null)}
          onSuccess={(pin) => doCheckout(pin)}
        />
      )}
    </div>
  );
}
